import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useRealtimeEvent, type UserEvent } from '@/features/realtime/RealtimeProvider';

import { chatKeys, chatMessagesKey } from './useChatSessions';

/**
 * Throttle window for the shared list-tab invalidation. A busy chat fans out
 * many `chat.message` events per second; invalidating the list on each would,
 * in prod (30 list reqs/min/user), trip the rate limit. Leading + trailing:
 * the first event in a quiet window refetches the lists immediately, further
 * events within the window collapse into a single trailing refetch. Per-chat
 * message invalidation stays immediate so the open thread never lags.
 */
const LIST_INVALIDATE_THROTTLE_MS = 4_000;

/**
 * App-root listener for the chat invalidation graph. Events are invalidation
 * signals only (payloads never carry message content); receivers refetch
 * over REST:
 *
 * - `chat.message`        → the conversation's message page + both list tabs
 *                           (preview / lastMessageAt / waiting changed).
 * - `chat.session-changed`→ the chat's detail (status flip), its message page
 *                           (timeout closes insert a system message), + lists.
 * - `chat.session-created`→ list tabs (a new row appeared). Nothing publishes
 *                           this today: the backend stopped announcing new
 *                           chats, and a first student message already
 *                           refreshes the same lists through `chat.message`.
 *                           The subscription stays as the wiring for any
 *                           future producer.
 *
 * No role gate: the backend only delivers chat events to the chat's student
 * and the eligible psychologists at its location.
 */
export function useChatChangedListener(): void {
  const queryClient = useQueryClient();

  // Shared leading + trailing throttle for the list-tab invalidation across all
  // three handlers, so a burst of events yields at most ~1 list refetch per
  // window (see LIST_INVALIDATE_THROTTLE_MS).
  const listTimerRef = useRef<number | null>(null);
  const listTrailingRef = useRef(false);

  const invalidateListsThrottled = useCallback(() => {
    if (listTimerRef.current !== null) {
      // Inside an open window: a refetch is owed when it closes.
      listTrailingRef.current = true;
      return;
    }
    // Leading edge: refetch now, then hold the window open. While events keep
    // arriving the timer re-arms and fires at most once per window; it clears
    // only after a quiet window with no owed trailing refetch.
    void queryClient.invalidateQueries({ queryKey: chatKeys.lists() });
    const openWindow = () => {
      listTimerRef.current = window.setTimeout(() => {
        if (listTrailingRef.current) {
          listTrailingRef.current = false;
          void queryClient.invalidateQueries({ queryKey: chatKeys.lists() });
          openWindow();
        } else {
          listTimerRef.current = null;
        }
      }, LIST_INVALIDATE_THROTTLE_MS);
    };
    openWindow();
  }, [queryClient]);

  useEffect(
    () => () => {
      if (listTimerRef.current !== null) window.clearTimeout(listTimerRef.current);
    },
    [],
  );

  const onMessage = useCallback(
    (event: Extract<UserEvent, { type: 'chat.message' }>) => {
      void queryClient.invalidateQueries({ queryKey: chatMessagesKey(event.chatSessionId) });
      invalidateListsThrottled();
    },
    [queryClient, invalidateListsThrottled],
  );
  useRealtimeEvent('chat.message', onMessage);

  const onSessionChanged = useCallback(
    (event: Extract<UserEvent, { type: 'chat.session-changed' }>) => {
      void queryClient.invalidateQueries({ queryKey: chatKeys.detail(event.chatSessionId) });
      void queryClient.invalidateQueries({ queryKey: chatMessagesKey(event.chatSessionId) });
      invalidateListsThrottled();
    },
    [queryClient, invalidateListsThrottled],
  );
  useRealtimeEvent('chat.session-changed', onSessionChanged);

  const onSessionCreated = useCallback(
    (_event: Extract<UserEvent, { type: 'chat.session-created' }>) => {
      invalidateListsThrottled();
    },
    [invalidateListsThrottled],
  );
  useRealtimeEvent('chat.session-created', onSessionCreated);
}
